import { generateClient, type Client, type GraphQLResult } from 'aws-amplify/api';
import { onAnyEventPushed } from '@env0/common-appsync-schema/graphql/subscriptions';
import { getLastEvent } from '@env0/common-appsync-schema/graphql/queries';
import type { SubscribeParameter } from 'stores/mobx/common/subscription-event-handler';
import { SubscriptionEventHandler } from 'stores/mobx/common/subscription-event-handler';
import type ServiceContainer from 'services/service-container';
import BaseService from 'services/base-service';
import { reportError } from 'utils/sentry.utils';
import type { GetLastEventQuery } from '@env0/common-appsync-schema/api';
import { Amplify } from 'aws-amplify';
import { amplifyConfig } from 'constants/config';
import isError from 'lodash/isError';

const isUnauthorized = (error: any) =>
  error?.errors?.some((e: any) => e.message?.toLowerCase().includes('unauthorized'));
const isTimeout = (error: any) => error?.errors?.some((e: any) => e.message?.toLowerCase().includes('timed out'));

// AppSync rejects a duplicate subscription operation id on a single connection (the original subscription keeps
// working) and surfaces transient WebSocket drops as "A network error has occurred." Both are expected/benign
// realtime churn the app recovers from, so they shouldn't be reported to Sentry as errors.
const isDuplicateOperation = (error: any) =>
  error?.errors?.some(
    (e: any) =>
      e?.errorType === 'DuplicatedOperationError' || e?.message?.toLowerCase().includes('duplicated operation')
  );
const isNetworkError = (error: any) =>
  error?.errors?.some((e: any) => e?.message?.toLowerCase().includes('network error')) ||
  error?.message?.toLowerCase().includes('network error');
const isExpectedSubscriptionError = (error: any) => isDuplicateOperation(error) || isNetworkError(error);

// Normalize various Amplify/AppSync error shapes into a proper Error with a meaningful message
const extractErrorMessage = (err: any): string =>
  err?.errors?.[0]?.message || err?.error?.message || err?.message || 'Unexpected error';

const toError = (err: unknown): Error =>
  isError(err) ? err : new Error(extractErrorMessage(err), { cause: err as any });

export class GraphqlEventsStore extends BaseService {
  private client: Client; // The generated Amplify API client instance
  subscribe({ stream, key, onEvent, skipInitEvent }: SubscribeParameter) {
    if (!key) return () => {};

    // A unique identifier for logging this specific subscription instance
    const subscriptionId = `${stream}:${key} (${Date.now()})`;

    let subscription: { unsubscribe: () => void } | undefined;
    const variables = { stream: `${stream}:${key}` };
    let canceled = false;

    const setupSubscription = async () => {
      try {
        const deltaResponse = (await this.executeGraphQLWithRetry({
          query: getLastEvent,
          variables
        })) as GraphQLResult<GetLastEventQuery>;

        const lastEvent = deltaResponse.data?.getLastEvent ?? null;

        this.subscriptionEventHandler.onDeltaQueryUpdate({
          event: lastEvent,
          onEvent,
          variables,
          skipInitEvent
        });

        // If canceled during the async delta fetch, stop here.
        if (canceled) {
          console.log(`[GraphQL] Canceled before establishing subscription.`, { id: subscriptionId });
          return;
        }

        subscription = this.client
          .graphql({
            query: onAnyEventPushed,
            variables
          }) // @ts-expect-error
          .subscribe({
            next: ({ data }: any) => {
              const event = data?.onAnyEventPushed;
              if (event) {
                this.subscriptionEventHandler.onSubscriptionQueryUpdate({
                  onAnyEventPushed: event,
                  onEvent,
                  variables
                });
              }
            },
            error: (error: any) => {
              if (isExpectedSubscriptionError(error)) {
                console.warn('[GraphQL] Expected subscription error, not reporting to Sentry', { stream, key, error });
                return;
              }
              reportError('Subscription', toError(error), { stream, key });
            }
          });
      } catch (error) {
        if (!isUnauthorized(error)) {
          reportError('SubscriptionSetup', toError(error), { stream, key });
        }
      }
    };

    if (this.service.authStore.getAccessToken()) {
      setupSubscription();
    } else {
      console.warn('Cannot subscribe to GraphQL events without an access token.');
    }

    // This is the cleanup function that gets returned immediately
    return () => {
      canceled = true;
      try {
        // The optional chaining ?. is key here
        subscription?.unsubscribe();
      } catch (e) {
        // It's also good practice to log errors during cleanup
        console.error(`[GraphQL] Error during unsubscribe.`, { id: subscriptionId, error: e });
      }
    };
  }
  /**
   * Helper to wrap GraphQL calls with a retry mechanism for token renewal.
   * Updated to use the v6 client instance.
   */
  private async executeGraphQLWithRetry(
    operation: { query: string; variables: object },
    retries = 1
  ): Promise<GraphQLResult<any>> {
    try {
      return await this.client.graphql(operation);
    } catch (error: any) {
      if (retries > 0 && isUnauthorized(error)) {
        await this.service.authStore.silentRenewSession();
        return this.executeGraphQLWithRetry(operation, retries - 1);
      }
      if (retries > 0 && isTimeout(error)) {
        return this.executeGraphQLWithRetry(operation, retries - 1);
      }
      throw error;
    }
  }

  constructor(
    service: ServiceContainer,
    private subscriptionEventHandler = new SubscriptionEventHandler()
  ) {
    super(service);
    Amplify.configure(amplifyConfig, {
      Auth: {
        tokenProvider: {
          getTokens: async () => {
            const accessToken = this.service.authStore.getAccessToken();
            if (!accessToken) {
              return null;
            }
            return { idToken: accessToken, accessToken: accessToken } as any;
          }
        }
      }
    });
    // @ts-expect-error — aws-amplify type inference exceeds stack depth in TS 5.x
    this.client = generateClient();
  }
}
